import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import {
  BulkCreateContactDocumentsDocument,
  FinanceContactDetailsPageDocumentsPageDocument,
  FinanceContactDocumentsDataCollectionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ContactDocumentsDataCollection from 'modules/finance/components/Contacts/ContactDocumentsDataCollection'
import DocumentsUploadWizard from 'modules/project_management/components/wizards/AddDocumentWizard/DocumentsUploadWizard'

const ContactsDetailsDocumentsPage = () => {
  const { contactId } = useParams()
  const { employee } = useCurrentSession()
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    track(trackingEvents.accounting.contactDocuments.visitContactDocumentsFilesTab)
  }, [])

  const [createContactDocument, { loading: createContactDocumentLoading }] = useMutation(
    BulkCreateContactDocumentsDocument,
    {
      extractorFn: (mutation) => mutation.financeMutations.bulkCreateFinanceContactDocument,
      refetchQueries: [
        FinanceContactDocumentsDataCollectionDocument,
        FinanceContactDetailsPageDocumentsPageDocument,
      ],
      awaitRefetchQueries: true,
    }
  )

  const handleSuccess = async (files: File[]) => {
    if (!contactId || !employee?.id) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('finance.contacts.files.upload_error'),
      })
      return
    }

    const result = await createContactDocument({
      variables: {
        files,
        contactId: Number(contactId),
        uploaderId: employee.id,
      },
    })

    track(trackingEvents.accounting.contactDocuments.clickOnUploadConfirmation)
    setShowModal(false)

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.contacts.files.upload_success'),
      })
      setShowModal(false)
    }
  }

  const { data } = useQuery(FinanceContactDetailsPageDocumentsPageDocument, {
    variables: {
      contactId: Number(contactId),
    },
  })

  const canManageDocuments = data?.finance._policies?.contactsManageBasicData.can || false

  const handleOnUploadClick = () => {
    setShowModal(true)
  }

  return (
    <>
      <Box gap="s24" paddingTop="s24">
        <ContactDocumentsDataCollection
          contactId={Number(contactId)}
          canManageDocuments={canManageDocuments}
          onClickUpload={handleOnUploadClick}
        />
      </Box>

      {showModal && (
        <DocumentsUploadWizard
          onClose={() => setShowModal(false)}
          onSuccess={handleSuccess}
          loading={createContactDocumentLoading}
        />
      )}
    </>
  )
}

export default ContactsDetailsDocumentsPage
